import { Box, Stack } from "@mui/material";
import { DamsAppAsset } from "../../assets/registry";
import { MainPageBase } from "../page-bases/MainPageBase";
import { ChangeEvent, useEffect, useRef, useState } from "react";

import DamsTable from "../../components/Table/CollapsibleTable";
import { ContainedButton } from "../../components/Button/AppButton";
import { NoItemContent } from "../page-bases/NoItemContent";
import { TRowItem } from "../../components/Table/TableRow";
import { ModalBase } from "../../components/Modals/ModalBase";
import ModalItemInput from "../../components/Input/ModalItemInput";
import { AssetTagApi } from "../../services/assets/assetTag";
import {
  AssetCategory,
  AssetCategoryDetail,
  AssetTagResponse,
  CreateAssetTag,
  IPagination,
  IQueryPerams,
  PageActions,
} from "../../config/types";
import { usePageModels } from "../../hooks/usePageActions";
import { FormField } from "../../utils/formSchema";
import useInputUpdateHelper from "../../hooks/useInputHelper";
import { useFieldHasError } from "../../hooks/useFieldHasError";
import ItemSelectInput from "../../components/Input/ItemSelectInput";
import useSelectHelper from "../../hooks/useSelectHelper";
import { isFormFilled } from "../../utils/validators";
import { BadgeLabel } from "../../components/Label/BadgeLabel";
import { useMangeTableItems } from "../../hooks/useManageTableItems";
import { usePagination } from "../../hooks/usePagination";
import { BulkDeleteController } from "../../common-ui/BulkDeleteController";
import SimpleLabel from "../../components/Label";
import { actionDialogStore, alertDialogStore } from "../../store/dialogs";
import { formatDate } from "../../utils/common";
import { AssetTagFilter } from "../../common-ui/page-filters/AssetTagFilter";
import ItemInput from "../../components/Input/ItemInput";

interface CreateAssetTagT extends FormField<CreateAssetTag> {
  submitAttempt?: boolean;
}

const __NEW_ASSET_TAG_DEFAULT_VALUE: CreateAssetTagT = {
  name: { isValid: false, value: "", errorMessage: "required" },
  asset_tag_field_type: { isValid: false, value: "", errorMessage: "required" },
};

interface IAssetTag {
  name: string;
}

function AssetTagsMainPage() {
  const [pageState, setPageState] = useState("main");
  const [dataset, setDataset] = useState<TRowItem[]>([]);

  const [pageAction, updatePageAction] = useState<PageActions>();
  const { models, closeModel, openModel } = usePageModels();
  const [isProcessing, setProcess] = useState(false);
  const isAssetTagForm = useRef<boolean>(false);
  const [AssetTagForm, updateAssetTagForm] = useState<CreateAssetTagT>(
    __NEW_ASSET_TAG_DEFAULT_VALUE
  );
  const [AssetTagOption, updateAssetTagOption] = useState<string>("");
  const [AssetTag, updateAssetTag] = useState<string>("");
  const [AssetTagOptionList, updateAssetTagOptionList] = useState<{
    submitAttempt?: boolean;
    data: string[];
  }>({ data: [] });
  const [previousTagsCart, setPreviousTagsCart] = useState<AssetTagResponse[]>(
    []
  );
  const [querySet, setQuerySet] = useState<IQueryPerams>({ is_deleted: false });
  const handleInputUpdate =
    useInputUpdateHelper<CreateAssetTagT>(updateAssetTagForm);

  const {
    selectedRowItems,
    toggleRow,
    toggleSelectAllRow,
    resetSelectedRowItems,
  } = useMangeTableItems(dataset);
  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState,
  } = usePagination<AssetTagResponse[]>(AssetTagApi.getAssetTags);

  useEffect(() => {
    fetchAssetTags();
  }, []);

  const populateDataSet = (
    data: AssetTagResponse[],
    pagination?: IPagination
  ) => {
    const formatData = data.map((item) => ({
      ...item,
      created_at: formatDate(item.created_at),
      _asset_tag_options_array: item.asset_tag_options,
      asset_tag_options:
        item.asset_tag_options && item.asset_tag_options.length > 0
          ? item.asset_tag_options.join(", ")
          : "None",
    }));
    setDataset(formatData);
    updatePaginationInitState(pagination);
  };

  function updateOptionList() {
    if (AssetTagOption === "") {
      return;
    }
    updateAssetTagOptionList((prev) => {
      if (prev.data.includes(AssetTagOption)) {
        return prev;
      }
      return { ...prev, data: [...prev.data, AssetTagOption] };
    });
    updateAssetTagOption("");
  }

  function removeTag(tagToRemove: string) {
    updateAssetTagOptionList((prev) => ({
      ...prev,
      data: prev.data.filter((tag) => tag !== tagToRemove),
    }));
  }

  const handleSelectUpdate =
    useSelectHelper<CreateAssetTagT>(updateAssetTagForm);
  const AssetTagFormHasError = useFieldHasError<CreateAssetTagT>(AssetTagForm);

  const page_info = getEmptyPageInfo(pageState);

  async function handleTagRequest() {
    updateAssetTagForm((prev) => ({ ...prev, submitAttempt: true }));
    const formValid = isFormFilled<CreateAssetTagT>(AssetTagForm);
    if (formValid) {
      setProcess(true);
      const data = {
        name: AssetTagForm.name.value,
        asset_tag_field_type: AssetTagForm.asset_tag_field_type.value,
        asset_tag_options:
          AssetTagOptionList.data.length > 0
            ? AssetTagOptionList.data
            : undefined,
      };
      const ftn =
        pageAction?.type === "edit"
          ? AssetTagApi.updateAssetTag(pageAction?.id ?? "", data)
          : AssetTagApi.createAssetTag(data);

      const res = await ftn;
      if (res && res.success) {
        await fetchAssetTags();
      }
      setProcess(false);
      alertDialogStore.open({
        subtitle: res?.message,
        title: `${pageAction?.type === "edit" ? "Update" : "New"} Asset Tag`,
        isSuccess: res?.success,
        onLeftBtnPress: handleModelClose,
        onRightBtnPress: handleModelClose,
      });
    }
  }

  async function handleAddAssetTagRequest() {
    updateAssetTagOptionList((prev) => ({ ...prev, submitAttempt: true }));
    const formValid = AssetTagOptionList.data.length > 0;
    if (formValid && pageAction?.id) {
      setProcess(true);
      const res = await AssetTagApi.createAssetTag({
        name: AssetTagForm.name.value,
        asset_tag_field_type: AssetTagForm.asset_tag_field_type.value,
        asset_tag_options: AssetTagForm.asset_tag_options?.value || [],
      });
      if (res && res.success) {
        await fetchAssetTags();
      }
      setProcess(false);
      alertDialogStore.open({
        subtitle: res?.message,
        title: "New Asset  Tags",
        isSuccess: res?.success,
      });
      updateAssetTagForm(__NEW_ASSET_TAG_DEFAULT_VALUE);
    }
  }

  function handleTagInput(event: ChangeEvent<HTMLInputElement>) {
    updateAssetTag(event.target.value);
  }

  function handleTagOptionInput(event: ChangeEvent<HTMLInputElement>) {
    updateAssetTagOption(event.target.value);
  }

  function addNewItem() {
    openModel("add");
    updatePageAction({
      type: "add",
    });
  }

  function clearSearch() {
    setPageState("empty-db");
  }

  const fetchAssetTags = async () => {
    const tags = await AssetTagApi.getAssetTags(querySet);

    if (tags.data) {
      populateDataSet(tags.data, tags.pagination);
      setPreviousTagsCart(
        tags.data.map((item) => ({
          ...item,
        }))
      );
    }
  };

  const handleModelClose = () => {
    setPreviousTagsCart([]);
    closeModel();
    updatePageAction({});
    updateAssetTag("");
    updateAssetTagOption("");
    updateAssetTagOptionList({ data: [] });
    updateAssetTagForm(__NEW_ASSET_TAG_DEFAULT_VALUE);
    isAssetTagForm.current = false;
  };

  function handleUpdateModel(item: TRowItem) {
    openModel("add");
    updatePageAction({
      id: item.id,
      name: item.name,
      type: "edit",
      data: item,
    });

    const options = (item as any)._asset_tag_options_array || [];

    updateAssetTagForm({
      asset_tag_field_type: {
        value: item.asset_tag_field_type,
        isValid: true,
      },
      name: {
        value: item.name,
        isValid: true,
      },
      asset_tag_options: {
        value: options,
        isValid: true,
      },
    });

    if (Array.isArray(options) && options.length > 0) {
      updateAssetTagOptionList({
        data: options,
        submitAttempt: false,
      });
    }
  }

  return (
    <MainPageBase
      title={"Asset Tags"}
      subtitle={""}
      pageState={pageState}
      addItemBtnOnClick={addNewItem}
      addItemBtnText={"Add Asset Tag"}
    >
      <Box
        height={`100%`}
        display={"flex"}
        alignItems={pageState !== "main" ? "center" : ""}
        justifyContent={"center"}
      >
        {pageState !== "main" ? (
          <NoItemContent
            type={pageState}
            title={page_info.title}
            subtitle={page_info.message}
            addItemBtn={addNewItem}
            clearSearchBtn={clearSearch}
          />
        ) : (
          <Stack width={"100%"} spacing={1}>
            <AssetTagFilter<AssetTagResponse[]>
              ftn={AssetTagApi.getAssetTags}
              updateDataSet={populateDataSet}
              updateQuerySet={setQuerySet}
            />
            <DamsTable
              tableTitle="Asset Tags"
              tableSubtitle=""
              numTableRecords="25"
              tableHeaders={columns}
              tableKeys={tkeys}
              hiddenKeys={hkeys}
              hiddenLabels={hcolumns}
              data={dataset}
              maxRowCells={3}
              useTableCheckbox
              onTableItemSelect={toggleRow}
              onTableItemEdit={handleUpdateModel}
              toggleSelectAllRow={toggleSelectAllRow}
              selectedRowItems={selectedRowItems}
              hasPagination
              paginationState={pagination}
              onNextPage={paginateToNextPage(querySet, populateDataSet)}
              onPreviousPage={paginateToPreviousPage(querySet, populateDataSet)}
              setActivePage={paginateToPageNumber(populateDataSet, querySet)}
            />
            {/* Add Asset Tags Modal */}
            <ModalBase
              open={models.showAddModel}
              title={`${
                pageAction?.type === "edit" ? "Update" : ""
              } Asset Tags Form`}
              subtitle={""}
              type={"add-item"}
              showHeader={true}
              leftIcon={DamsAppAsset.icons.devices}
              leftBtnTxt="Cancel"
              onLeftBtnPress={handleModelClose}
              rightBtnTxt={`${
                pageAction?.type === "edit" ? "Update" : "Add"
              } Tag`}
              onRightBtnPress={handleTagRequest}
              onRightBtnLoading={isProcessing}
              onClose={handleModelClose}
            >
              <Stack gap={2}>
                <ItemInput
                  size="full"
                  label={"Asset Tag Name"}
                  placeholder={"Enter Asset Tag Name"}
                  handleChange={handleInputUpdate("name")}
                  value={AssetTagForm.name?.value ?? ""}
                  error={AssetTagFormHasError("name")}
                  errorMessage={AssetTagForm.name?.errorMessage}
                />
                <ItemSelectInput
                  size="full"
                  label={"Asset Tag Field Type"}
                  item={""}
                  placeholder={"Select Asset Field type"}
                  items={ASSET_TAG_FIELD_TYPE_ITEM_LIST}
                  handleChange={handleSelectUpdate("asset_tag_field_type")}
                  value={AssetTagForm.asset_tag_field_type?.value ?? ""}
                  error={AssetTagFormHasError("asset_tag_field_type")}
                  errorMessage={AssetTagForm.asset_tag_field_type?.errorMessage}
                />

                {(String(AssetTagForm.asset_tag_field_type?.value) ===
                  "single-select" ||
                  String(AssetTagForm.asset_tag_field_type?.value) ===
                    "multi-select") && (
                  <>
                    <SimpleLabel
                      title={
                        AssetTagOptionList.data.length === 0
                          ? "No options added yet"
                          : `Options (${AssetTagOptionList.data.length})`
                      }
                    />
                    <Stack
                      gap={2}
                      direction={"row"}
                      justifyContent={"center"}
                      alignItems={"center"}
                    >
                      <ModalItemInput
                        label="Asset Tags Options"
                        placeholder="Enter Asset Tags Options"
                        handleChange={handleTagOptionInput}
                        value={AssetTagOption}
                      />
                      <Box marginTop={3}>
                        <ContainedButton
                          title="Add"
                          onClick={updateOptionList}
                        />
                      </Box>
                    </Stack>
                    <Box minHeight={50} marginTop={3}>
                      {AssetTagOptionList.data.map((tag, index) => (
                        <BadgeLabel
                          key={index}
                          margin={0.5}
                          onClick={() => removeTag(tag)}
                          title={tag}
                          colorType="green"
                          isDeleteAction
                        />
                      ))}
                    </Box>
                  </>
                )}
              </Stack>
            </ModalBase>

            {/* Add Asset Tag Modal */}
            <ModalBase
              width={500}
              open={models.showAddModel && isAssetTagForm.current}
              title={`${pageAction?.name} Asset Tags`}
              subtitle={"Manage Tags"}
              type={"add-item"}
              showHeader={true}
              leftIcon={DamsAppAsset.icons.devices}
              leftBtnTxt="Cancel"
              onLeftBtnPress={handleModelClose}
              rightBtnTxt="Continue"
              onRightBtnPress={handleAddAssetTagRequest}
              onRightBtnLoading={isProcessing}
              onClose={handleModelClose}
            >
              <SimpleLabel
                title={
                  previousTagsCart.length === 0
                    ? "No tags added yet"
                    : "Current Tags"
                }
              />
              <Box minHeight={50}>
                {previousTagsCart.map((tag, index) => (
                  <BadgeLabel
                    key={index}
                    margin={0.5}
                    title={tag.name}
                    colorType="green"
                    isDeleteAction
                  />
                ))}
              </Box>
              <Box minHeight={50} marginTop={3}>
                {AssetTagOptionList.data.map((tag, index) => (
                  <BadgeLabel
                    key={index}
                    margin={0.5}
                    onClick={() => removeTag(tag)}
                    title={tag}
                    colorType="blue"
                    isDeleteAction
                  />
                ))}
              </Box>
            </ModalBase>
          </Stack>
        )}
      </Box>
    </MainPageBase>
  );
}

const getEmptyPageInfo = (key: string): { title: string; message: string } => {
  if (page_msg.hasOwnProperty(key)) {
    return page_msg[key];
  } else {
    return { title: "", message: "" };
  }
};

const page_msg: { [key: string]: { title: string; message: string } } = {
  "empty-db": {
    title: "No Asset Tags Found",
    message:
      'No Asset Tags have been added yet. Click the "Add New" button to add a new Asset Tag.',
  },

  "empty-search": {
    title: "No Asset Tags Found",
    message:
      "No Asset Tags found in your database. Please add a new Asset Tag to find them in the Asset Tags tab ",
  },
};

export default AssetTagsMainPage;

const ASSET_TAG_FIELD_TYPE_ITEM_LIST = [
  { label: "Single select", value: "single-select" },
  { label: "Multiple select", value: "multi-select" },
  { label: "Text input", value: "text-input" },
  { label: "Number input", value: "number-input" },
];

const tkeys = ["name", "asset_tag_field_type", "created_at"];
const columns = ["Asset Tag Name", "Asset Tag Field Type", "Date Added"];

const hkeys = ["asset_tag_options"];
const hcolumns = ["Asset Tag Options"];
